import { useEffect } from "react";

// Port of the v25 "SNT" engine with performance tiers. blur scales every shadowBlur in the frame.
export const TIERS = {
  low: { name: "low", particles: 1000, blur: 0, dprCap: 1, dust: 80, trails: false },
  mid: { name: "mid", particles: 1800, blur: 0.5, dprCap: 1.25, dust: 140, trails: true },
  high: { name: "high", particles: 3000, blur: 1, dprCap: 1.5, dust: 220, trails: true },
};
const ORDER = ["low", "mid", "high"];
const TAU = Math.PI * 2;
const PHASE_EDGES = [0.16, 0.32, 0.48, 0.62, 0.7];
const KEYS = ["a", "k", "v", "g", "e", "n"];
const mix = (a, b, t) => a + (b - a) * t;
const ease = (t) => t * t * (3 - 2 * t);
const clamp01 = (t) => Math.max(0, Math.min(1, t));
const seeded = (i, n = 0) => { const x = Math.sin(i * 127.1 + n * 311.7) * 43758.5453123; return x - Math.floor(x); };
const IS_SAFARI = /^((?!chrome|android|crios|fxios).)*safari/i.test(navigator.userAgent);

export function detectTier() {
  const dpr = window.devicePixelRatio || 1;
  const cores = navigator.hardwareConcurrency || 4;
  const mem = navigator.deviceMemory || 4;
  const small = Math.min(window.innerWidth, window.innerHeight) < 600;
  let score = 0;
  if (cores >= 8) score += 1;
  if (cores <= 4) score -= 1;
  if (mem >= 8) score += 1;
  if (mem <= 2) score -= 1;
  if (dpr > 2.5) score -= 1;
  if (small) score -= 1;
  return score >= 1 ? "high" : score >= 0 ? "mid" : "low";
}

function makeSprite(r, g, b, hot) {
  const s = 64, c = document.createElement("canvas");
  c.width = c.height = s;
  const x = c.getContext("2d");
  const grad = x.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
  grad.addColorStop(0, hot ? "rgba(255,255,255,1)" : "rgba(235,250,255,.95)");
  grad.addColorStop(0.16, `rgba(${r},${g},${b},.85)`);
  grad.addColorStop(0.45, `rgba(${r},${g},${b},.28)`);
  grad.addColorStop(1, `rgba(${r},${g},${b},0)`);
  x.fillStyle = grad;
  x.fillRect(0, 0, s, s);
  return c;
}

const SHAPES = {
  a(i, N, S) { // sphere
    const u = (i + 0.5) / N, v = (i * 0.61803398875) % 1, phi = Math.acos(1 - 2 * u), th = TAU * v, r = 215 * S;
    return [Math.sin(phi) * Math.cos(th) * r, Math.cos(phi) * r, Math.sin(phi) * Math.sin(th) * r];
  },
  k(i, N, S) { // torus knot
    const tt = (i / N) * TAU + seeded(i, 1) * 0.14, R = 135 * S, tube = (44 + seeded(i, 2) * 22) * S;
    return [(R + tube * Math.cos(3 * tt)) * Math.cos(2 * tt), (R + tube * Math.cos(3 * tt)) * Math.sin(2 * tt), tube * Math.sin(3 * tt)];
  },
  v(i, N, S) { // vortex
    const strand = i % 14, t = ((i / 14) | 0) / (N / 14), ang = (strand / 14) * TAU + t * TAU * 2.5, r = (55 + 185 * t) * S;
    return [Math.cos(ang) * r + (seeded(i, 3) - 0.5) * 16 * S, (t - 0.5) * 440 * S, Math.sin(ang) * r + (seeded(i, 4) - 0.5) * 16 * S];
  },
  g(i, N, S) { // galaxy
    const arm = i % 2, t = ((i / 2) | 0) / (N / 2), ang = t * TAU * 2.2 + arm * Math.PI, r = (22 + 255 * t) * S;
    const x = Math.cos(ang) * r, z = Math.sin(ang) * r * 0.6, y = (Math.sin(t * 18 + arm) * 12 + (seeded(i, 5) - 0.5) * 34) * S;
    const tilt = 0.55;
    return [x, y * Math.cos(tilt) - z * Math.sin(tilt), y * Math.sin(tilt) + z * Math.cos(tilt)];
  },
  e(i, N, S) { // burst
    const u = seeded(i, 7), v = seeded(i, 8), phi = Math.acos(1 - 2 * u), th = TAU * v, r = (200 + seeded(i, 9) * 480) * S;
    return [Math.sin(phi) * Math.cos(th) * r, (seeded(i, 10) - 0.5) * 560 * S, Math.sin(phi) * Math.sin(th) * r];
  },
  n(i, N, S) { // nexus lattice
    if (seeded(i, 50) < 0.55) {
      const V = [[1, 1, 1], [1, -1, -1], [-1, 1, -1], [-1, -1, 1]];
      const E = [[0, 1], [0, 2], [0, 3], [1, 2], [1, 3], [2, 3]];
      const inv = seeded(i, 53) < 0.5, e = E[(seeded(i, 51) * 6) | 0], t = seeded(i, 52);
      const a = V[e[0]], b = V[e[1]], s = (190 + seeded(i, 54) * 14) * S * (inv ? -1 : 1);
      return [s * (a[0] + (b[0] - a[0]) * t), s * (a[1] + (b[1] - a[1]) * t), s * (a[2] + (b[2] - a[2]) * t)];
    }
    const ring = (seeded(i, 55) * 6) | 0, a = seeded(i, 56) * TAU, r = (150 + ring * 46) * S;
    return [Math.cos(a) * r, ((ring - 2.5) * 28 + Math.sin(a * 4 + ring) * 12) * S, Math.sin(a) * r];
  },
};

function buildParticles(N, S) {
  const P = { N, p: new Float32Array(N * 3), vx: new Float32Array(N), vy: new Float32Array(N), seed: new Float32Array(N) };
  KEYS.forEach((k) => { P[k] = new Float32Array(N * 3); });
  for (let i = 0; i < N; i++) {
    KEYS.forEach((k) => P[k].set(SHAPES[k](i, N, S), i * 3));
    for (let c = 0; c < 3; c++) P.p[i * 3 + c] = P.a[i * 3 + c] * 1.35;
    P.seed[i] = seeded(i, 40);
  }
  return P;
}

function lerpInto(out, A, B, j, t) {
  const k = ease(clamp01(t));
  out[0] = mix(A[j], B[j], k); out[1] = mix(A[j + 1], B[j + 1], k); out[2] = mix(A[j + 2], B[j + 2], k);
}
function targetInto(P, j, s, out) {
  if (s < 0.16) { out[0] = P.a[j]; out[1] = P.a[j + 1]; out[2] = P.a[j + 2]; }
  else if (s < 0.32) lerpInto(out, P.a, P.k, j, (s - 0.16) / 0.16);
  else if (s < 0.48) lerpInto(out, P.k, P.v, j, (s - 0.32) / 0.16);
  else if (s < 0.62) lerpInto(out, P.v, P.g, j, (s - 0.48) / 0.14);
  else if (s < 0.68) lerpInto(out, P.g, P.e, j, (s - 0.62) / 0.06);
  else if (s < 0.7) { out[0] = P.e[j]; out[1] = P.e[j + 1]; out[2] = P.e[j + 2]; }
  else lerpInto(out, P.e, P.n, j, (s - 0.7) / 0.3);
}
const phaseMix = (s) => (s < 0.16 ? 0.16 : s < 0.32 ? 0.38 : s < 0.48 ? 0.52 : s < 0.62 ? 0.8 : s < 0.7 ? 0.6 : 0.46);
const wobbleAmp = (s) => (s < 0.16 ? 15 : s < 0.32 ? 6 : s < 0.48 ? 13 : s < 0.62 ? 9 : s < 0.7 ? 24 : 6);

export function useNexusCanvas(canvasRef, enabled) {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!enabled || !canvas) return undefined;
    const ctx = canvas.getContext("2d");
    const SCALE = window.matchMedia("(max-width: 820px)").matches ? 1.0 : 1.18;
    const spCyan = makeSprite(96, 200, 255), spViolet = makeSprite(178, 120, 255), spWhite = makeSprite(235, 245, 255, true);
    let tier = TIERS[detectTier()];
    let blurK = IS_SAFARI ? 0 : tier.blur;
    let P = buildParticles(tier.particles, SCALE);
    let W = 0, H = 0, DPR = 1, bgGrad = null, anchors = [], heroDrift = [], heroFade = [], lastHf = -1;
    let scroll = 0, targetScroll = 0, velocity = 0, lastScroll = 0, burstEnergy = 0, flash = 0, lastPhase = 0;
    let raf = 0, running = false, drawErrs = 0, lastFps = 0;
    const t0 = performance.now();
    const ripples = [];
    const mouse = { x: 0, y: 0, px: 0, py: 0, gx: 0, gy: 0, active: false };
    const probe = { start: 0, frames: 0, done: false };
    const Q = [0, 0, 0];

    function anchorInterp(key) {
      if (!anchors.length) return key === "s" ? 0 : 1;
      const y = window.scrollY + window.innerHeight * 0.5;
      if (y <= anchors[0].y) return anchors[0][key];
      for (let i = 0; i < anchors.length - 1; i++) {
        const A = anchors[i], B = anchors[i + 1];
        if (y <= B.y) return A[key] + (B[key] - A[key]) * ease((y - A.y) / Math.max(1, B.y - A.y));
      }
      return anchors[anchors.length - 1][key];
    }

    function measure() {
      anchors = [...document.querySelectorAll("[data-nexus-s]")]
        .map((el) => ({ y: el.getBoundingClientRect().top + window.scrollY + el.offsetHeight * 0.5, s: Number(el.dataset.nexusS), d: Number(el.dataset.nexusDim || 1) }))
        .sort((a, b) => a.y - b.y);
      heroDrift = [...document.querySelectorAll("[data-hero-drift]")];
      heroFade = [...document.querySelectorAll("[data-hero-fade]")];
      lastHf = -1;
      targetScroll = lastScroll = anchorInterp("s");
    }

    function resize() {
      DPR = Math.min(window.devicePixelRatio || 1, tier.dprCap);
      W = window.innerWidth;
      H = window.innerHeight;
      canvas.width = Math.round(W * DPR);
      canvas.height = Math.round(H * DPR);
      bgGrad = ctx.createRadialGradient(W / 2, H * 0.52, 0, W / 2, H * 0.52, Math.max(W, H) * 0.8);
      bgGrad.addColorStop(0, "#0a2440");
      bgGrad.addColorStop(0.4, "#040d18");
      bgGrad.addColorStop(1, "#010308");
      measure();
    }

    function setTier(next) {
      tier = next;
      blurK = IS_SAFARI ? 0 : tier.blur;
      P = buildParticles(tier.particles, SCALE);
      for (let i = 0; i < P.N; i++) { targetInto(P, i * 3, scroll, Q); P.p.set(Q, i * 3); }
      canvas.dataset.tier = tier.name;
      resize();
    }

    function probeTick(t) {
      if (probe.done) return;
      if (!probe.start) { probe.start = t; probe.frames = 0; return; }
      probe.frames += 1;
      const elapsed = t - probe.start;
      if (elapsed < 1000) return;
      lastFps = Math.round((probe.frames * 1000) / elapsed);
      canvas.dataset.fps = String(lastFps);
      const idx = ORDER.indexOf(tier.name);
      if (lastFps < 45 && idx > 0) { setTier(TIERS[ORDER[idx - 1]]); probe.start = 0; } else probe.done = true;
    }

    function ring(cx, cy, r, squash, rotAngle, a0, a1, style, blur) {
      ctx.save(); ctx.translate(cx, cy); ctx.rotate(rotAngle); ctx.scale(1, squash);
      ctx.beginPath(); ctx.arc(0, 0, r, a0, a1);
      ctx.strokeStyle = style; ctx.lineWidth = 1;
      if (blur * blurK) { ctx.shadowColor = style; ctx.shadowBlur = blur * blurK; }
      ctx.stroke(); ctx.restore();
    }

    function heroChoreo() {
      const hf = clamp01(window.scrollY / (window.innerHeight * 0.75));
      if (hf === lastHf) return;
      lastHf = hf;
      const ty = `0 ${-Math.min(window.scrollY, window.innerHeight * 1.5) * 0.1}px`;
      heroDrift.forEach((el) => { el.style.translate = ty; el.style.opacity = String(1 - hf * 0.9); });
      heroFade.forEach((el) => { el.style.opacity = String(1 - hf * 0.95); });
    }

    function draw(time) {
      scroll += (targetScroll - scroll) * 0.31;
      ctx.globalCompositeOperation = "source-over";
      ctx.globalAlpha = 1;
      ctx.shadowBlur = 0;
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
      ctx.fillStyle = bgGrad; ctx.fillRect(0, 0, W, H);
      burstEnergy *= 0.91; velocity *= 0.86; flash *= 0.93;

      const intro = clamp01((time - t0) / 2200), introBoost = 1 + (1 - intro) * 8;
      let phase = 0;
      PHASE_EDGES.forEach((e) => { if (scroll > e) phase += 1; });
      if (phase !== lastPhase) { flash = 1; lastPhase = phase; }

      const s = scroll, dim = anchorInterp("d");
      const qs = 0.76 + 0.24 * dim, pa = 0.22 + 0.78 * dim, la = dim * dim, coreA = 0.55 + 0.45 * dim, tracerA = 0.15 + 0.85 * dim;
      const corePhase = clamp01((s - 0.7) / 0.3);
      const explodePhase = clamp01((s - 0.62) / 0.06) * (1 - clamp01((s - 0.68) / 0.02));
      const cx = W / 2 + mouse.x * 26, cy = mix(H * 0.55, H * 0.67, ease(clamp01((s - 0.75) / 0.25))) + mouse.y * 18;
      const rot = time * (0.00022 + corePhase * 0.00018) + mouse.x * 0.38 + scroll * 2.4;
      const corePulse = 0.5 + 0.5 * Math.sin(time * 0.0052);
      heroChoreo();

      mouse.gx += (mouse.px - mouse.gx) * 0.12;
      mouse.gy += (mouse.py - mouse.gy) * 0.12;
      if (mouse.active) {
        ctx.save(); ctx.globalCompositeOperation = "lighter"; ctx.globalAlpha = 0.12 * la;
        ctx.drawImage(spCyan, mouse.gx - 75, mouse.gy - 75, 150, 150);
        ctx.restore(); ctx.globalAlpha = 1;
      }

      // nebula washes
      ctx.save(); ctx.globalCompositeOperation = "lighter";
      const n1x = cx + Math.sin(time * 0.00011) * W * 0.16, n1y = cy + Math.cos(time * 0.00013) * H * 0.1, k1 = SCALE * qs;
      ctx.globalAlpha = 0.07 * la; ctx.drawImage(spViolet, n1x - 340 * k1, n1y - 300 * k1, 680 * k1, 600 * k1);
      ctx.globalAlpha = 0.06 * la; ctx.drawImage(spCyan, cx - Math.sin(time * 0.00009) * W * 0.2 - 300 * k1, cy - 40 - Math.sin(time * 0.00012) * H * 0.08, 600 * k1, 560 * k1);
      ctx.restore(); ctx.globalAlpha = 1;

      // core orb + rings
      ctx.save(); ctx.globalCompositeOperation = "lighter";
      const cr = (62 + corePulse * 8 + corePhase * 14) * 2.5 * (0.72 + 0.28 * dim) * (1 + flash * 0.45);
      ctx.globalAlpha = 0.9 * coreA; ctx.drawImage(spViolet, cx - cr, cy - cr, cr * 2, cr * 2);
      ctx.globalAlpha = 0.95 * coreA; ctx.drawImage(spCyan, cx - cr * 0.46, cy - cr * 0.46, cr * 0.92, cr * 0.92);
      ctx.globalAlpha = coreA; ctx.drawImage(spWhite, cx - cr * 0.13, cy - cr * 0.13, cr * 0.26, cr * 0.26);
      for (let k = 0; k < 4; k++) {
        ring(cx, cy, (78 + k * 30) * SCALE * qs, 0.3 + k * 0.09, time * (0.00042 + k * 0.00011) * (k % 2 ? 1 : -1) + k * 1.2 + scroll,
          -1.2 + k * 0.3, 1.6 + k * 0.5, `rgba(${k % 2 ? 188 : 100},${k % 2 ? 130 : 210},255,${(0.16 + k * 0.02 + corePhase * 0.05 + flash * 0.12) * coreA})`, 18);
      }
      ctx.restore();

      // energy ribbons
      const ribAlpha = (0.04 + 0.07 * ease(clamp01((s - 0.2) / 0.5))) * la;
      ctx.save(); ctx.translate(cx, cy); ctx.globalCompositeOperation = "lighter";
      for (let r = 0; r < 5; r++) {
        ctx.save();
        ctx.rotate(time * (0.00012 + r * 0.00004) * (r % 2 ? -1 : 1) + r * 1.26);
        ctx.scale(1, 0.28 + r * 0.06);
        ctx.beginPath();
        const rr = (120 + r * 42) * SCALE * qs;
        for (let k = 0; k <= 64; k++) {
          const a = (k / 64) * TAU, rad = rr + Math.sin(a * 3 + time * 0.0011 + r) * 22 * SCALE * qs;
          if (k === 0) ctx.moveTo(Math.cos(a) * rad, Math.sin(a) * rad); else ctx.lineTo(Math.cos(a) * rad, Math.sin(a) * rad);
        }
        ctx.strokeStyle = r % 2 ? `rgba(178,120,255,${ribAlpha})` : `rgba(96,200,255,${ribAlpha * 1.15})`;
        ctx.lineWidth = 1.1;
        if (blurK) { ctx.shadowColor = r % 2 ? "#a678ff" : "#4ec8ff"; ctx.shadowBlur = 10 * blurK; }
        ctx.stroke();
        ctx.restore();
      }
      ctx.restore();

      // tap / click ripples
      if (ripples.length) {
        ctx.save(); ctx.globalCompositeOperation = "lighter";
        for (let i = ripples.length - 1; i >= 0; i--) {
          const rp = ripples[i], age = (time - rp.t) / 900;
          if (age >= 1) { ripples.splice(i, 1); continue; }
          const w1 = ease(age);
          ctx.beginPath(); ctx.arc(rp.x, rp.y, 20 + w1 * 300, 0, TAU);
          ctx.strokeStyle = `rgba(96,205,255,${(1 - w1) * 0.45})`; ctx.lineWidth = 1.3 + 2.6 * (1 - w1);
          ctx.shadowColor = "#45c6ff"; ctx.shadowBlur = 20 * blurK; ctx.stroke();
          if (age > 0.15) {
            const w2 = ease(clamp01((age - 0.15) / 0.85));
            ctx.beginPath(); ctx.arc(rp.x, rp.y, 12 + w2 * 260, 0, TAU);
            ctx.strokeStyle = `rgba(178,120,255,${(1 - w2) * 0.4})`; ctx.lineWidth = 1.1 + 2.2 * (1 - w2);
            ctx.shadowColor = "#a678ff"; ctx.shadowBlur = 18 * blurK; ctx.stroke();
          }
        }
        ctx.restore();
      }

      // orbital tracer field
      ctx.save(); ctx.translate(cx, cy + 215 * SCALE * qs); ctx.scale(1, 0.2); ctx.globalCompositeOperation = "lighter";
      for (let ri = 0; ri < 12; ri++) {
        const r = (86 + ri * 36) * SCALE * qs;
        ctx.beginPath(); ctx.arc(0, 0, r, 0, TAU);
        ctx.strokeStyle = `rgba(56,157,230,${(0.04 + 0.02 * (0.5 + 0.5 * Math.sin(time * 0.0013 + ri * 0.67))) * (1 - ri / 15) * tracerA})`;
        ctx.lineWidth = 0.62; ctx.stroke();
        if (ri % 3 === 0 || ri === 7) {
          const dir = ri % 2 ? -1 : 1, a = time * (0.00032 + ri * 0.000011) * dir + ri * 0.83;
          if (tier.trails) {
            for (let ti = 30; ti > 0; ti--) {
              const a1 = a - dir * ti * 0.0135, a2 = a - dir * (ti - 1) * 0.0135, fade = 1 - ti / 31;
              ctx.beginPath(); ctx.arc(0, 0, r, Math.min(a1, a2), Math.max(a1, a2));
              ctx.strokeStyle = ri === 7 ? `rgba(170,116,255,${(0.01 + 0.17 * fade * fade) * tracerA})` : `rgba(83,207,255,${(0.01 + 0.2 * fade * fade) * tracerA})`;
              ctx.lineWidth = 0.45 + 1.2 * fade; ctx.stroke();
            }
          }
          ctx.globalAlpha = 0.95 * tracerA;
          ctx.drawImage(ri === 7 ? spViolet : spCyan, Math.cos(a) * r - 8, Math.sin(a) * r - 8, 16, 16);
          ctx.globalAlpha = 1;
        }
      }
      ctx.restore();

      // dust
      for (let i = 0; i < tier.dust; i++) {
        const x = (i * 137.5 + Math.sin(time * 0.00012 + i) * 20) % W, y = (i * 83.7 + Math.cos(time * 0.0001 + i * 1.7) * 12) % H;
        ctx.fillStyle = `rgba(120,205,255,${(0.02 + 0.06 * (0.5 + 0.5 * Math.sin(time * 0.0007 + i))) * (0.3 + 0.7 * dim)})`;
        ctx.fillRect(x, y, 0.6, 0.6);
      }

      // supernova shockwave
      if (explodePhase > 0 && explodePhase < 1) {
        const w1 = ease(explodePhase), w2 = ease(clamp01(explodePhase - 0.18)), m = Math.min(W, H);
        ctx.save(); ctx.globalCompositeOperation = "lighter";
        ctx.beginPath(); ctx.arc(cx, cy, 80 + w1 * m * 0.5, 0, TAU);
        ctx.strokeStyle = `rgba(90,205,255,${(1 - w1) * 0.4})`; ctx.lineWidth = 1.4 + 3.4 * (1 - w1);
        ctx.shadowColor = "#45c6ff"; ctx.shadowBlur = 26 * blurK; ctx.stroke();
        if (w2 > 0) {
          ctx.beginPath(); ctx.arc(cx, cy, 60 + w2 * m * 0.52, 0, TAU);
          ctx.strokeStyle = `rgba(178,120,255,${(1 - w2) * 0.34})`; ctx.lineWidth = 1.2 + 3 * (1 - w2);
          ctx.shadowColor = "#a678ff"; ctx.shadowBlur = 24 * blurK; ctx.stroke();
        }
        ctx.restore();
      }

      // nexus halo
      if (corePhase > 0.05) {
        const ca = Math.min(1, (corePhase - 0.05) / 0.24) * (0.4 + 0.6 * dim);
        ctx.save(); ctx.translate(cx, cy); ctx.globalCompositeOperation = "lighter";
        for (let k = 0; k < 4; k++) {
          ctx.save(); ctx.rotate(time * (0.0001 + k * 0.000022) * (k % 2 ? -1 : 1) + k * 0.72); ctx.scale(1, 0.34 + k * 0.045);
          ctx.beginPath(); ctx.arc(0, 0, (190 + k * 48) * SCALE * qs, 0, TAU);
          ctx.setLineDash([2 + k, 9 + k * 3]); ctx.lineDashOffset = -time * 0.015 * (k % 2 ? 1 : -1);
          ctx.strokeStyle = `rgba(${k % 2 ? 150 : 67},${k % 2 ? 112 : 195},255,${(0.06 + k * 0.009) * ca})`;
          ctx.lineWidth = 1; ctx.stroke(); ctx.restore();
        }
        ctx.setLineDash([]); ctx.restore();
      }

      // particles
      const baseFollow = s > 0.7 ? 0.16 + velocity * 0.3 : 0.14 + burstEnergy * 0.18;
      const follow = 1 - Math.pow(1 - Math.min(0.6, baseFollow), introBoost);
      const pm = phaseMix(s), wob = wobbleAmp(s) * (1 + burstEnergy * 0.6) * (0.6 + 0.4 * dim);
      const cR = Math.cos(rot), sR = Math.sin(rot);
      const { p: pp, vx, vy, seed: seeds, N } = P;
      ctx.save(); ctx.globalCompositeOperation = "lighter";
      for (let i = 0; i < N; i++) {
        const j = i * 3, seed = seeds[i];
        targetInto(P, j, s, Q);
        const k2 = (1 + Math.sin(time * 0.0026 + seed * 6.2) * (0.008 + burstEnergy * 0.02)) * qs;
        pp[j] += (Q[0] * k2 - pp[j]) * follow;
        pp[j + 1] += (Q[1] * k2 - pp[j + 1]) * follow;
        pp[j + 2] += (Q[2] * k2 - pp[j + 2]) * follow;
        const x = pp[j] + Math.sin(pp[j + 1] * 0.012 + time * 0.0013 + seed * 6.28) * wob;
        const y = pp[j + 1] + Math.sin(pp[j] * 0.011 + time * 0.0011 + seed * 4.0) * wob;
        const z = pp[j + 2] + Math.sin((pp[j] + pp[j + 1]) * 0.008 + time * 0.0009 + seed * 2.1) * wob * 0.6;
        const xr = x * cR - z * sR, zr = x * sR + z * cR;
        const persp = Math.min(1.5, Math.max(0.3, 560 / (700 + zr)));
        let sx = cx + xr * persp, sy = cy + y * persp;
        if (mouse.active) {
          const dx = sx - mouse.px, dy = sy - mouse.py, d2 = dx * dx + dy * dy;
          if (d2 < 28900 && d2 > 0.01) { const d = Math.sqrt(d2), f = (1 - d / 170) * 2.4; vx[i] += (dx / d) * f; vy[i] += (dy / d) * f; }
        }
        vx[i] *= 0.9; vy[i] *= 0.9;
        sx += vx[i]; sy += vy[i];
        const depth = Math.max(0.12, Math.min(1, 0.62 - zr / 560)), sparkle = seed > 0.965;
        const size = Math.min(24, (2.4 + depth * 7.5) * (0.6 + seed) * (sparkle ? 1.9 : 1) * (0.7 + 0.3 * dim));
        ctx.globalAlpha = (sparkle ? Math.max(0.55, pa) : 0.2 + 0.72 * depth) * pa;
        ctx.drawImage(sparkle ? spWhite : (pm + (seed - 0.5) * 0.3 < 0.5 ? spCyan : spViolet), sx - size / 2, sy - size / 2, size, size);
      }
      ctx.restore(); ctx.globalAlpha = 1;

      // data streaks
      if (corePhase > 0.12) {
        ctx.save(); ctx.globalCompositeOperation = "lighter"; ctx.translate(cx, cy);
        for (let j = 0; j < 9; j++) {
          const a = time * 0.0012 * (j % 2 ? 1 : -1) + j * 0.71, r = (150 + j * 20) * SCALE * qs;
          ctx.globalAlpha = (0.2 + 0.13 * Math.sin(a * 2)) * tracerA;
          ctx.drawImage(j % 3 === 0 ? spViolet : spCyan, Math.cos(a) * r - 4.5, Math.sin(a) * r * 0.32 - 4.5, 9, 9);
        }
        ctx.restore(); ctx.globalAlpha = 1;
      }
    }

    function frame(t) {
      raf = requestAnimationFrame(frame);
      probeTick(t);
      try { draw(t); } catch (err) {
        drawErrs += 1;
        ctx.globalCompositeOperation = "source-over"; ctx.globalAlpha = 1; ctx.shadowBlur = 0;
      }
    }

    const start = () => {
      if (running) return;
      running = true;
      if (!probe.done) probe.start = 0;
      raf = requestAnimationFrame(frame);
    };
    const stop = () => { running = false; cancelAnimationFrame(raf); };
    // Scroll velocity drives the burst, so touch scrolling gets the same climax as a wheel.
    const onScroll = () => {
      const ns = anchorInterp("s");
      velocity = Math.min(0.16, Math.abs(ns - lastScroll) * 4.6);
      burstEnergy = Math.min(1, burstEnergy + velocity * 12);
      lastScroll = ns;
      targetScroll = ns;
    };
    const onMove = (e) => { mouse.x = e.clientX / W - 0.5; mouse.y = e.clientY / H - 0.5; mouse.px = e.clientX; mouse.py = e.clientY; mouse.active = true; };
    const onLeave = (e) => { if (e.type === "pointerleave" || e.pointerType === "touch") mouse.active = false; };
    const onDown = (e) => {
      if (e.target.closest("header, a, button, input, textarea, select, label, form, [role=dialog]")) return;
      ripples.push({ x: e.clientX, y: e.clientY, t: performance.now() });
      if (ripples.length > 4) ripples.shift();
      burstEnergy = 1;
    };
    const onVisibility = () => (document.hidden ? stop() : start());
    let resizeTimer = 0;
    const onResize = () => { clearTimeout(resizeTimer); resizeTimer = setTimeout(resize, 150); };
    const ro = new ResizeObserver(onResize);

    let dbg = null, dbgTimer = 0;
    if (new URLSearchParams(window.location.search).has("debug")) {
      dbg = document.createElement("div");
      dbg.className = "nexus-debug";
      document.body.appendChild(dbg);
      dbgTimer = setInterval(() => {
        dbg.textContent = `tier ${tier.name} | N ${P.N} | dpr ${DPR} | blur ${blurK} | fps ${lastFps} | s ${scroll.toFixed(2)} | dim ${anchorInterp("d").toFixed(2)} | ${running ? "running" : "paused"} | drawErr ${drawErrs}`;
      }, 1000);
    }

    canvas.dataset.tier = tier.name;
    resize();
    scroll = targetScroll;
    if (!document.hidden) start();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("pointerup", onLeave, { passive: true });
    window.addEventListener("pointercancel", onLeave, { passive: true });
    document.documentElement.addEventListener("pointerleave", onLeave);
    window.addEventListener("pointerdown", onDown, { passive: true });
    window.addEventListener("resize", onResize);
    document.addEventListener("visibilitychange", onVisibility);
    ro.observe(document.body);

    return () => {
      stop();
      clearTimeout(resizeTimer);
      clearInterval(dbgTimer);
      if (dbg) dbg.remove();
      ro.disconnect();
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onLeave);
      window.removeEventListener("pointercancel", onLeave);
      document.documentElement.removeEventListener("pointerleave", onLeave);
      window.removeEventListener("pointerdown", onDown);
      window.removeEventListener("resize", onResize);
      document.removeEventListener("visibilitychange", onVisibility);
      [...document.querySelectorAll("[data-hero-drift],[data-hero-fade]")].forEach((el) => { el.style.translate = ""; el.style.opacity = ""; });
    };
  }, [canvasRef, enabled]);
}
